import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Spin,
  message,
} from 'antd';
import {
  DeleteOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { MAX_CUENTAS, encolarSaldosMasivos } from 'api/procesosMasivos/saldosMasivos';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import Select from 'components/Select';
import useConfirm from 'hooks/useConfirm';
import usePermissionMessage from 'hooks/usePermissionMessage';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import LoteDeCuentas, {
  MSG_MAXIMO,
  MSG_SIN_PERMISO,
  useLoteDeCuentas,
} from './components/LoteDeCuentas';

export const permissionConfig = {
  mainPermission: 'view_saldosmasivos',
  required: ['view_predio'],
  customActions: [
    {
      name: 'consultar',
      permissions: ['view_saldosmasivos', 'view_predio'],
    },
  ],
};

const BIMESTRES = [1, 2, 3, 4, 5, 6];

const MSG_LOTE_VACIO = 'Debe agregar al menos una cuenta antes de consultar';
const MSG_INICIADO = 'Procesamiento de saldos masivos iniciado correctamente. Recibirá un correo con el resultado al finalizar.';

const hoy = new Date();
const initialValues = {
  anio: hoy.getFullYear(),
  bimestre: Math.floor(hoy.getMonth() / 2) + 1,
  tipo_de_padron: padronesEnum.PREDIO,
};

const SaldosMasivos = ({ permission }) => {
  const puedeOperar = permission.hasAllViewPermission && permission.consultar;
  usePermissionMessage(permission);

  const [form] = Form.useForm();
  const lote = useLoteDeCuentas({ form, puedeOperar });
  const [ConfirmModal, confirm] = useConfirm('Confirmación', '¿Estás seguro de querer generar la consulta?', {
    alert: 'Esta acción puede tardar varios minutos en completarse.',
  });

  const [loading, setLoading] = useState(false);

  const rules = {
    anio: [{ required: true, message: 'Capture el año' }],
    bimestre: [{ required: true, message: 'Seleccione el bimestre' }],
  };

  const onConsultar = async () => {
    if (!puedeOperar) {
      message.warning(MSG_SIN_PERMISO);
      return;
    }
    const { cuentasDelLote } = lote;
    if (!cuentasDelLote.length) {
      message.warning(MSG_LOTE_VACIO);
      return;
    }
    if (cuentasDelLote.length > MAX_CUENTAS) {
      message.warning(MSG_MAXIMO);
      return;
    }
    let values;
    try {
      values = await form.validateFields(['anio', 'bimestre']);
    } catch {
      return;
    }
    const confirmed = await confirm();
    if (!confirmed) return;
    setLoading(true);
    const result = await encolarSaldosMasivos({
      anio: values.anio,
      bimestre: values.bimestre,
      cuentas: cuentasDelLote,
    });
    setLoading(false);
    if (result) message.success(MSG_INICIADO);
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin spinning={loading || lote.loading} tip="Cargando...">
        <Card
          className="form-container"
          bordered={false}
          title="Búsqueda del Padrón"
          extra={(
            <FormSubmitCustomControls
              loading={loading || lote.loading}
              controls={[{
                text: 'Limpiar Búsqueda',
                icon: <DeleteOutlined />,
                onClick: lote.limpiar,
              }]}
            />
          )}
        >
          <LoteDeCuentas
            lote={lote}
            permission={permission}
            formName="saldos-masivos"
            initialValues={initialValues}
            camposExtra={(
              <>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="anio" label="Año" rules={rules.anio}>
                    <InputNumber
                      min={1900}
                      max={9999}
                      precision={0}
                      style={{ width: '100%' }}
                      disabled={!puedeOperar}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="bimestre" label="Bimestre" rules={rules.bimestre}>
                    <Select dataSource={BIMESTRES} allowClear={false} disabled={!puedeOperar} />
                  </Form.Item>
                </Col>
              </>
            )}
          />

          <br />
          <Row gutter={10} justify="end">
            <Col xs={24} sm={24} md={8} lg={6}>
              <Button
                block
                icon={<SendOutlined />}
                onClick={onConsultar}
                disabled={!puedeOperar}
              >
                Consultar
              </Button>
            </Col>
          </Row>
        </Card>
        <ConfirmModal />
      </Spin>
    </Row>
  );
};

SaldosMasivos.propTypes = {
  permission: PropTypes.shape({
    hasAllViewPermission: PropTypes.bool,
    consultar: PropTypes.bool,
    view_predio: PropTypes.bool,
    view_saldosmasivos: PropTypes.bool,
  }).isRequired,
};

export default SaldosMasivos;
